import React, { useEffect, useMemo, useState } from "react";
import {
  Table,
  Button,
  Input,
  Form,
  message,
  Modal,
  Spin,
  Tooltip,
  Upload,
  Progress,
  Tag,
  Space,
  Divider,
} from "antd";
import {
  DeleteOutlined,
  PlusOutlined,
  SearchOutlined,
  UploadOutlined,
  ExclamationCircleOutlined,
  FolderOpenOutlined,
  LinkOutlined,
} from "@ant-design/icons";
import axiosInstance from "../utils/axiosInstance";

const { confirm } = Modal;
const { Dragger } = Upload;

export default function DownloadsAdmin() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadPercent, setUploadPercent] = useState(0);

  const [searchText, setSearchText] = useState("");
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [filesModalVisible, setFilesModalVisible] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [uploadFileList, setUploadFileList] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [form] = Form.useForm();

  const pageSize = 10;

  const formatFolders = (folders = []) =>
    folders.map((folder) => ({
      key: folder.id,
      id: folder.id,
      title: folder.title,
      category: folder.category,
      description: folder.description,
      files: folder.files || [],
      filesCount: folder.files?.length || 0,
      createdAt: folder.createdAt,
    }));

  const fetchFolders = async () => {
    setLoading(true);

    try {
      const res = await axiosInstance.get("/admin/downloads");

      if (res.data?.success) {
        const formatted = formatFolders(res.data.folders || []);
        setList(formatted);
        return formatted;
      }

      setList([]);
      message.error(res.data?.message || "Failed to load folders");
      return [];
    } catch (err) {
      console.error("fetchFolders error:", err);
      message.error(err.response?.data?.message || "Error loading folders");
      setList([]);
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFolders();
  }, []);

  const handleAdd = async (values) => {
    setLoading(true);

    try {
      const res = await axiosInstance.post("/admin/downloads/folder", values);

      if (res.data?.success) {
        message.success("Folder created");
        form.resetFields();
        setAddModalVisible(false);
        await fetchFolders();
      } else {
        message.error(res.data?.message || "Failed to create folder");
      }
    } catch (err) {
      console.error("handleAdd error:", err);
      message.error(err.response?.data?.message || "Network error");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (id) => {
    confirm({
      title: "Delete this folder?",
      icon: <ExclamationCircleOutlined />,
      content:
        "This will delete this folder record and all download files linked with it.",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        setLoading(true);

        try {
          const res = await axiosInstance.delete(`/admin/downloads/folder/${id}`);

          if (res.data?.success) {
            message.success("Folder deleted");
            await fetchFolders();
          } else {
            message.error(res.data?.message || "Failed to delete folder");
          }
        } catch (err) {
          console.error("handleDelete error:", err);
          message.error(err.response?.data?.message || "Delete failed");
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const openFilesModal = (record) => {
    setSelectedFolder(record);
    setUploadFileList([]);
    setUploadPercent(0);
    setFilesModalVisible(true);
  };

  const closeFilesModal = () => {
    if (uploading) return;

    setFilesModalVisible(false);
    setSelectedFolder(null);
    setUploadFileList([]);
    setUploadPercent(0);
  };

  const handleUploadSelectedFiles = async () => {
    if (!selectedFolder?.id) {
      message.error("Please select a folder first");
      return;
    }

    if (!uploadFileList.length) {
      message.error("Please select at least one file");
      return;
    }

    const formData = new FormData();

    uploadFileList.forEach((fileItem) => {
      const rawFile = fileItem.originFileObj || fileItem;
      formData.append("files", rawFile);
    });

    formData.append("folderId", selectedFolder.id);

    setUploading(true);
    setUploadPercent(0);

    try {
      const res = await axiosInstance.post("/admin/downloads/files", formData, {
        onUploadProgress: (event) => {
          if (event.total > 0) {
            setUploadPercent(Math.round((event.loaded / event.total) * 100));
          }
        },
      });

      if (res.data?.success) {
        const failed = res.data?.failed || [];

        if (failed.length) {
          message.warning("Some files uploaded, but some failed");
        } else {
          message.success("Uploaded to OneDrive");
        }

        setUploadFileList([]);
        setUploadPercent(100);

        const updatedFolders = await fetchFolders();
        const updatedSelectedFolder = updatedFolders.find(
          (folder) => folder.id === selectedFolder.id
        );

        if (updatedSelectedFolder) {
          setSelectedFolder(updatedSelectedFolder);
        }
      } else {
        message.error(res.data?.message || "Upload failed");
      }
    } catch (err) {
      console.error("Upload error:", err);
      message.error(err.response?.data?.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const filtered = useMemo(() => {
    return list.filter((item) =>
      `${item.title || ""} ${item.category || ""}`
        .toLowerCase()
        .includes(searchText.toLowerCase())
    );
  }, [list, searchText]);

  const paginatedData = filtered.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const getFileTypeColor = (type) => {
    if (type === "image") return "green";
    if (type === "pdf") return "red";
    if (type === "video") return "blue";
    return "default";
  };

  const folderColumns = [
    {
      title: "Sr.No",
      width: 70,
      render: (_, __, index) => (
        <div className="text-center">
          {(currentPage - 1) * pageSize + index + 1}
        </div>
      ),
    },
    {
      title: "Folder Name",
      dataIndex: "title",
      render: (title) => <div className="font-medium">{title}</div>,
    },
    {
      title: "Category",
      dataIndex: "category",
      render: (value) => value || "-",
    },
    {
      title: "Files",
      dataIndex: "filesCount",
      width: 100,
      align: "center",
    },
    {
      title: "Actions",
      width: 220,
      align: "center",
      render: (_, record) => (
        <Space>
          <Tooltip title="View and upload files">
            <Button
              icon={<FolderOpenOutlined />}
              size="small"
              type="primary"
              onClick={() => openFilesModal(record)}
            >
              Files
            </Button>
          </Tooltip>

          <Tooltip title="Delete folder">
            <Button
              danger
              icon={<DeleteOutlined />}
              size="small"
              onClick={() => handleDelete(record.id)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const filesColumns = [
    {
      title: "File Name",
      dataIndex: "file_name",
      render: (name) => <span className="font-medium">{name}</span>,
    },
    {
      title: "Type",
      dataIndex: "file_type",
      width: 100,
      render: (type) => (
        <Tag color={getFileTypeColor(type)}>{type || "file"}</Tag>
      ),
    },
    {
      title: "Storage",
      dataIndex: "storage_provider",
      width: 120,
      render: (provider) => <Tag>{provider || "-"}</Tag>,
    },
    {
      title: "Open",
      width: 90,
      align: "center",
      render: (_, record) =>
        record.file_url ? (
          <a href={record.file_url} target="_blank" rel="noreferrer">
            <LinkOutlined /> Open
          </a>
        ) : (
          "-"
        ),
    },
  ];

  return (
    <Spin spinning={loading} size="large">
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-6 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Downloads Manager
            </h1>
            <p className="text-gray-600">
              Manage folders, images, videos, and PDF downloads for customers.
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <SearchOutlined className="mr-2" />
                  Search folders
                </label>

                <Input
                  placeholder="Search by name or category..."
                  value={searchText}
                  onChange={(e) => {
                    setSearchText(e.target.value);
                    setCurrentPage(1);
                  }}
                  allowClear
                  className="h-10 rounded-lg"
                />
              </div>

              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-2">
                  Folder Summary
                </label>

                <div className="h-10 px-4 rounded-lg border border-gray-200 bg-gray-50 flex items-center">
                  <span className="text-sm text-gray-700">
                    Total: <strong>{filtered.length}</strong> folders
                  </span>
                </div>
              </div>

              <div className="flex items-end">
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setAddModalVisible(true)}
                  className="h-10 bg-blue-600 hover:bg-blue-700 border-blue-600"
                >
                  Create Folder
                </Button>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-200">
            <Table
              dataSource={paginatedData}
              columns={folderColumns}
              rowKey="id"
              pagination={{
                current: currentPage,
                pageSize,
                total: filtered.length,
                onChange: (page) => setCurrentPage(page),
                showSizeChanger: false,
              }}
              locale={{
                emptyText: "No folders created yet",
              }}
            />
          </div>
        </div>
      </div>

      <Modal
        title="Create New Folder"
        open={addModalVisible}
        onCancel={() => {
          setAddModalVisible(false);
          form.resetFields();
        }}
        footer={null}
      >
        <Form layout="vertical" form={form} onFinish={handleAdd}>
          <Form.Item
            label="Folder Name"
            name="title"
            rules={[{ required: true, message: "Folder name required" }]}
          >
            <Input />
          </Form.Item>

          <Form.Item label="Category" name="category">
            <Input />
          </Form.Item>

          <Form.Item label="Description" name="description">
            <Input.TextArea rows={3} />
          </Form.Item>

          <div className="flex justify-end gap-3">
            <Button
              onClick={() => {
                setAddModalVisible(false);
                form.resetFields();
              }}
            >
              Cancel
            </Button>

            <Button type="primary" htmlType="submit">
              Create
            </Button>
          </div>
        </Form>
      </Modal>

      <Modal
        title={
          selectedFolder
            ? `Manage Files - ${selectedFolder.title}`
            : "Manage Files"
        }
        open={filesModalVisible}
        onCancel={closeFilesModal}
        width={900}
        footer={[
          <Button key="close" onClick={closeFilesModal} disabled={uploading}>
            Close
          </Button>,
          <Button
            key="upload"
            type="primary"
            icon={<UploadOutlined />}
            loading={uploading}
            disabled={!uploadFileList.length}
            onClick={handleUploadSelectedFiles}
          >
            Upload Selected Files
          </Button>,
        ]}
      >
        <div>
          <h3 className="font-semibold mb-2">Existing Files</h3>

          <Table
            dataSource={selectedFolder?.files || []}
            columns={filesColumns}
            rowKey={(record) => record.id}
            size="small"
            pagination={{
              pageSize: 5,
              showSizeChanger: false,
            }}
            locale={{
              emptyText: "No files uploaded in this folder yet",
            }}
          />

          <Divider />

          <h3 className="font-semibold mb-2">Upload New Files</h3>

          <Dragger
            multiple
            accept="image/*,video/*,application/pdf"
            beforeUpload={() => false}
            fileList={uploadFileList}
            onChange={({ fileList }) => {
              setUploadFileList(fileList);
              setUploadPercent(0);
            }}
            onRemove={(file) => {
              setUploadFileList((prev) =>
                prev.filter((item) => item.uid !== file.uid)
              );
            }}
            disabled={uploading}
          >
            <p className="ant-upload-drag-icon">
              <UploadOutlined />
            </p>
            <p className="ant-upload-text">
              Click or drag image, video, or PDF files here
            </p>
            <p className="ant-upload-hint">
              Supported: JPG, PNG, WEBP, PDF, MP4 and other image/video files.
            </p>
          </Dragger>

          {uploading && (
            <div style={{ marginTop: 16 }}>
              <Progress percent={uploadPercent} />
            </div>
          )}
        </div>
      </Modal>
    </Spin>
  );
}